import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
  updateHeaderHeights,
  updateAllHeaderCustomProperties,
  debounce,
} from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Accumulated up-scroll distance (px) since the last downward scroll, when
   * sticky behavior is 'scroll-up'. The header reveals only once this passes
   * #revealThreshold, so tiny upward jitters don't flash it in.
   * @type {number}
   */
  #upDistance = 0;

  /**
   * Minimum accumulated up-scroll (px) before the scroll-up header reveals.
   * @type {number}
   */
  static #revealThreshold = 48;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Cached announcement-bar height (group total minus the header row). The
   * announcement doesn't resize while the header logo compacts, so caching it
   * lets the ResizeObserver keep --header-group-height in sync using only the
   * size it's already handed — no getComputedStyle/offsetHeight reflow per frame.
   * @type {number}
   */
  #announcement = 0;

  /**
   * Tallest (un-compacted) header height seen at the current viewport width.
   * Published as `--header-height-expanded` so the first section's transparent
   * header offset stays fixed while the header compacts on scroll — otherwise
   * vertically centred hero content drifts by half the compaction.
   * @type {number}
   */
  #expandedHeight = 0;

  /** Viewport width #expandedHeight was measured at (reset on width change). @type {number} */
  #expandedWidth = 0;

  /**
   * Keeps the global `--header-height` / `--header-group-height` custom properties
   * up to date as the header resizes (e.g. the logo compacting on scroll).
   * Reflow-free: uses the ResizeObserver's reported border-box size + the cached
   * announcement height, so the CSS height transition stays smooth.
   */
  /**
   * Publishes the measured header height to the global custom properties.
   * Debounced: --header-height / --header-group-height are inherited custom
   * properties on <body>, so each write invalidates style for the whole document
   * subtree. Writing them on every frame of the CSS height compaction caused a
   * ~0.5s "Recalculate Style" storm per scroll (the CPU spike). The compaction is
   * a CSS transition (the visual is unaffected); the published value only needs
   * to be correct once the size settles, so we coalesce the writes.
   * @param {number} headerHeight
   */
  #publishHeaderHeight = debounce((headerHeight) => {
    document.body.style.setProperty('--header-height', `${headerHeight}px`);

    this.#trackExpandedHeight(headerHeight);

    // On transparent pages the header is an absolute overlay, so it's excluded
    // from the group height (which stays = announcement only).
    if (!this.hasAttribute('transparent')) {
      document.body.style.setProperty('--header-group-height', `${headerHeight + this.#announcement}px`);
    }
  }, 100);

  /** @param {number} headerHeight */
  #trackExpandedHeight(headerHeight) {
    if (window.innerWidth !== this.#expandedWidth) {
      this.#expandedWidth = window.innerWidth;
      this.#expandedHeight = 0;
    }
    if (headerHeight > this.#expandedHeight) {
      this.#expandedHeight = headerHeight;
      document.body.style.setProperty('--header-height-expanded', `${headerHeight}px`);
    }
  }

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const headerHeight = Math.round(entry.borderBoxSize[0].blockSize);
    this.#publishHeaderHeight(headerHeight);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Recomputes everything (full measure) and caches the announcement height.
   * Call on connect and when the header group itself changes — not per frame.
   */
  refreshHeaderMetrics = () => {
    updateHeaderHeights();
    this.#trackExpandedHeight(this.offsetHeight);
    this.#announcement = parseFloat(getComputedStyle(document.body).getPropertyValue('--announcement-height')) || 0;
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * Document-space bottom of the first content section. The scroll-up header
   * stays visible through the first section and only begins hiding past it.
   * @returns {number}
   */
  #firstSectionBottom() {
    // TEST: fixed 25dvh threshold instead of the first section's bottom.
    // To revert, restore the first-section measurement:
    const first = document.querySelector('.content-for-layout > .shopify-section, main > .shopify-section');
    if (!first) return window.innerHeight;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    return first.getBoundingClientRect().bottom + scrollTop;
  }

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // scroll-up mode.
    const engaged = scrollTop > this.#firstSectionBottom();

    if (!engaged) {
      // Within the first section: always visible, never slid away. Still does
      // the normal one-time compaction as it leaves the very top.
      this.removeAttribute('data-hidden');
      this.#offscreen = false;
      this.#upDistance = 0;
      this.dataset.stickyState = scrollTop <= 0 ? 'inactive' : 'active';
      this.dataset.scrollDirection = scrollTop <= 0 ? 'none' : isScrollingUp ? 'up' : 'down';
      this.#lastScrollTop = scrollTop;
      return;
    }

    // Past the first section: stay compact (no re-shrink) and slide on scroll —
    // hidden (translated up) going down, revealed going up. Require a little
    // accumulated up-scroll before revealing so a 1px jitter doesn't flash it in.
    this.#offscreen = true;
    this.dataset.stickyState = 'active';

    const delta = this.#lastScrollTop - scrollTop;
    if (delta > 0) {
      // Scrolling up: accumulate, reveal only once past the threshold.
      this.#upDistance += delta;
      if (this.#upDistance >= HeaderComponent.#revealThreshold) {
        this.removeAttribute('data-hidden');
      }
      this.dataset.scrollDirection = 'up';
    } else if (delta < 0) {
      // Scrolling down: hide immediately, reset the up accumulator.
      this.#upDistance = 0;
      this.setAttribute('data-hidden', '');
      this.dataset.scrollDirection = 'down';
    }

    this.#lastScrollTop = scrollTop;
  };

  #desktopQuery = window.matchMedia('(min-width: 750px)');

  /**
   * Transparent header limited to one viewport (`data-transparent-viewport`,
   * sections/header.liquid). Liquid renders it transparent and an inline script
   * strips that pre-paint on the other viewport; this re-applies or strips it when
   * the viewport crosses the breakpoint, then recomputes the header offsets.
   */
  #applyTransparentViewport = () => {
    const viewport = this.dataset.transparentViewport;
    if (!viewport) return;

    const enabled = (viewport === 'desktop') === this.#desktopQuery.matches;
    const schemeClass = this.dataset.transparentClass;

    if (enabled) {
      this.setAttribute('transparent', this.dataset.transparentValue || 'always');
      if (schemeClass) this.classList.add(schemeClass);
      if (this.hasAttribute('data-transparent-pre-shrink')) this.setAttribute('data-pre-shrink', '');
    } else {
      this.removeAttribute('transparent');
      this.removeAttribute('data-pre-shrink');
      if (schemeClass) this.classList.remove(schemeClass);
    }

    updateAllHeaderCustomProperties();
  };

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.transparentViewport) {
      this.#desktopQuery.addEventListener('change', this.#applyTransparentViewport);
    }
    this.refreshHeaderMetrics();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktopQuery.removeEventListener('change', this.#applyTransparentViewport);
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Refresh metrics when a NON-header header-group child resizes (e.g. the
  // announcement bar). The header element itself is handled by the component's
  // own (reflow-free) ResizeObserver, so it's deliberately not observed here —
  // observing it would re-trigger a full measure on every logo-compaction frame.
  const refresh = () => {
    if (header && typeof (/** @type {any} */ (header).refreshHeaderMetrics) === 'function') {
      /** @type {any} */ (header).refreshHeaderMetrics();
    } else {
      updateHeaderHeights();
    }
  };

  if (headerGroup && header instanceof HTMLElement) {
    const resizeObserver = new ResizeObserver(refresh);

    const observeChildren = () => {
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (element instanceof HTMLElement && !element.contains(header)) {
          resizeObserver.observe(element);
        }
      }
    };

    observeChildren();

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') observeChildren();
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
